import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import CampaignCard from "../../../components/common/CampaignCard/CampaignCard";
import CampaignCardSkeleton from "../../../components/common/CampaignCard/CampaignCardSkeleton";
import { SwipeRow, HINDI_FONT } from "../../Temples/shared";
import { getFeaturedTemples } from "../../../api/services/templesService";
import headingLines from "../../../assets/Images/TempleDetail/heading-lines.svg";
import headingLotus from "../../../assets/Images/TempleDetail/heading-lotus.svg";
import illustrationLeft from "../../../assets/Images/TempleThankYou/illustration-left.svg";
import mandalaHalf from "../../../assets/Images/TempleThankYou/mandala-half.svg";

// Shared by the temple thank-you page (TempleThankYou) and the temple payment-failed page
// (PaymentFailurePage/TempleFailure): colours, amount format, summary detail cell, pill buttons
// and the "Temples that need your urgent attention" section.

export const C = {
  ink: "#1D1D1B",
  red: "#EC1B26",
  gold: "#C9952B",
  muted: "#7D7D7D",
  body: "#6B6560",
  cream: "#FBF5EC",
  sand: "#E4DCCF",
};
export const FONT = "Red Hat Display";

export const money = (n, currency = "INR") => {
  const v = Number(n) || 0;
  if (currency !== "INR") return `${currency} ${v.toLocaleString("en-US")}`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
};

// phone: Figma "Mobile revamp" 802:1709 — 10px label, 12px value, 3px apart
export const Detail = ({ label, value, phone = false }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: phone ? "3px" : "4px", minWidth: 0 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: "10px", lineHeight: phone ? "normal" : "13px", letterSpacing: phone ? "0.8px" : "1px", color: C.muted }}>{label}</Typography>
    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: phone ? "12px" : "14px", lineHeight: phone ? "normal" : "20px", color: C.ink, wordBreak: "break-word" }}>{value || "—"}</Typography>
  </Box>
);

// Donor / date / transaction / payment. Phones: two per row, wrapping (Figma 802:1709 — flex wrap,
// 14px between rows); tablets two columns; desktop one row of four.
export const DetailsGrid = ({ phone, isDesktop, children }) => (
  <Box
    sx={
      phone
        ? { display: "flex", flexWrap: "wrap", rowGap: "14px", "& > *": { flex: "0 0 50%", maxWidth: "50%", pr: "8px", boxSizing: "border-box" } }
        : { display: "grid", gridTemplateColumns: isDesktop ? "minmax(0, 1.15fr) minmax(0, 0.95fr) minmax(0, 1.5fr) minmax(0, 0.6fr)" : "1fr 1fr", rowGap: "16px", columnGap: "16px" }
    }
  >
    {children}
  </Box>
);

export const pillSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  pl: "24px",
  pr: "26px",
  py: "14px",
  borderRadius: "100px",
  cursor: "pointer",
  fontFamily: FONT,
  fontWeight: 700,
  fontSize: "14px",
  lineHeight: "20px",
  whiteSpace: "nowrap",
  transition: "transform 0.15s, box-shadow 0.15s",
  "&:hover": { transform: "translateY(-1px)" },
  "&:focus-visible": { outline: `2px solid ${C.ink}`, outlineOffset: "2px" },
};

// Phones: full-width buttons stacked 10px apart (Figma 802:1722)
export const pillPhoneSx = {
  width: "100%",
  boxSizing: "border-box",
  justifyContent: "center",
  gap: "8px",
  pl: "16px",
  pr: "16px",
  py: "14px",
  fontSize: "14px",
  lineHeight: "normal",
};

// Featured temples other than campaignId (up to 3), in the gold-bordered section.
export const UrgentTemples = ({ campaignId }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [temples, setTemples] = useState([]);
  const [templesLoading, setTemplesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getFeaturedTemples()
      .then((list) => !cancelled && setTemples(list.filter((t) => String(t.id) !== String(campaignId)).slice(0, 3)))
      .catch(() => !cancelled && setTemples([]))
      .finally(() => !cancelled && setTemplesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  const cards = templesLoading
    ? Array.from({ length: 3 }, (_, i) => <CampaignCardSkeleton key={i} isCampaign />)
    : temples.map((t) => <CampaignCard key={t.id} campaignData={t} isCampaign />);

  if (!templesLoading && temples.length === 0) return null;
  return (
      <Box sx={{ px: isMobile ? "12px" : "30px", pb: isMobile ? "48px" : "40px" }}>
        <Box
          component="section"
          sx={{
            position: "relative",
            maxWidth: "1380px",
            mx: "auto",
            borderRadius: isMobile ? "20px" : "24px",
            border: "1px solid rgba(201,149,43,0.25)",
            background: "linear-gradient(180deg, #FDF9F2 0%, #FFFDF9 100%)",
            overflow: "hidden",
            pt: isMobile ? "40px" : "49px",
            pb: isMobile ? "28px" : "50px",
          }}
        >
          {!isMobile && (
            <>
              <Box component="img" src={mandalaHalf} alt="" sx={{ position: "absolute", left: "50%", top: "-50px", transform: "translateX(-50%)", width: "500px", pointerEvents: "none" }} />
              <Box component="img" src={illustrationLeft} alt="" sx={{ position: "absolute", left: "70px", top: "24px", width: "230px", height: "170px", pointerEvents: "none" }} />
              <Box component="svg" viewBox="0 0 26 22" sx={{ position: "absolute", right: "31px", top: "40px", width: "26px", height: "22px", pointerEvents: "none" }} fill="none">
                <path d="M1 3C4 0.6 7 0.6 10 3C13 0.6 16 0.6 19 3" stroke="#C9952B" strokeWidth="1.2" strokeLinecap="round" opacity="0.6" />
                <path d="M6 20C8.5 18 11 18 13.5 20C16 18 18.5 18 21 20" stroke="#C9952B" strokeWidth="1.2" strokeLinecap="round" opacity="0.6" />
              </Box>
            </>
          )}
          <Box sx={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", textAlign: "center", px: "20px" }}>
            <Typography sx={{ fontFamily: HINDI_FONT, fontSize: isMobile ? "20px" : "24px", lineHeight: "22px", color: C.gold }}>॥ सेवा ॥</Typography>
            <Typography component="h2" sx={{ fontFamily: FONT, fontWeight: 700, fontSize: isMobile ? "22px" : "32px", lineHeight: isMobile ? "30px" : "40px", color: C.ink, mt: "8px" }}>
              Temples that need your urgent attention
            </Typography>
            <Box sx={{ position: "relative", width: "120px", height: "14px" }}>
              <Box component="img" src={headingLines} alt="" sx={{ position: "absolute", left: 0, top: "1.4px", width: "120px", height: "11.1px" }} />
              <Box component="img" src={headingLotus} alt="" sx={{ position: "absolute", left: "46.3px", top: "5.3px", width: "27.3px", height: "3.3px" }} />
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: "15px", lineHeight: "24px", color: C.body, maxWidth: "640px" }}>
              These sacred spaces are running out of time. Your offering today can keep their doors open.
            </Typography>
          </Box>
          <Box sx={{ position: "relative", mt: isMobile ? "24px" : "24px" }}>
            {isMobile ? (
              <SwipeRow itemWidth={300} px={16}>{cards}</SwipeRow>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, 417px)", justifyContent: "center", columnGap: "16px", rowGap: "24px", px: "40px" }}>{cards}</Box>
            )}
          </Box>
        </Box>
      </Box>
  );
};
